import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { isCell } from '@/lib/cell/config';
import { cellGet } from '@/lib/cell/api';
import { useSession } from '@/lib/auth';
import { useActiveLocationId } from '@/lib/locationContext';
import { useStudioFacts, type StudioFacts } from '@/lib/studioFacts';
import { IMAGE_LIBRARY, handbookPath, libraryPaths, signLibrary, talksFor } from '@/lib/proLibrary';

/**
 * Documents — Pro-facing knowledge base for IMAGE Studios.
 *
 * Lists the studio's reference docs as cards. Each card opens the
 * document in a new tab. Every entry comes from MY STUDIO — see
 * docsFromStudio below and lib/studioFacts.ts.
 *
 * Per ADR-022 (Hub stays simple): one tap to open, no in-app
 * reader. Operating-system handles HTML / PDF rendering.
 */

type DocIcon = 'book' | 'shield' | 'file' | 'star';

type DocEntry = {
  title: string;
  description: string;
  url: string;
  icon?: DocIcon;
};


/**
 * DOCUMENTS COME FROM MY STUDIO (Peter, 2026-08-26). My Studio's own "Your
 * documents" section already holds handbook_url, security_doc_enabled and
 * security_policy_url — an owner sets them there and nowhere else. This page
 * used to ignore all three and show a hardcoded pair of SHARON's files to every
 * studio's pros, including the security document, which is Peter's own and
 * which My Studio has an explicit switch for.
 *
 * There is no platform default any more: a studio that has published nothing
 * gets the honest empty state below, not another studio's paperwork.
 */
function docsFromStudio(f: StudioFacts, handbookSigned: string | null,
                        signedFor: (p?: string) => string | null): DocEntry[] {
  const out: DocEntry[] = [];
  // THE STUDIO'S OWN HANDBOOK. The platform renders one per studio — page 22 of
  // corporate's 2026 master filled from THIS studio's My Studio facts — and it
  // lives in the private library, signed at view time. A studio that hosts its
  // own elsewhere sets handbook_url in My Studio, and that wins: My Studio
  // stays the source, this is just where the rendered one lives.
  const handbook = f.handbookUrl ?? handbookSigned;
  if (handbook) {
    out.push({
      title: 'IMAGE Pro Handbook',
      description: 'Facility policies, guidelines, core values, hours, cleaning, wifi, deliveries — everything you need to know about life at the studio.',
      url: handbook,
      icon: 'book',
    });
  }
  // The switch is the owner's, and it is respected: no toggle, no document.
  if (f.securityDocEnabled && f.securityPolicyUrl) {
    out.push({
      title: 'Security & After-Hours Procedures',
      description: 'Access control, alarm codes, after-hours guest entry, late-night operations, and emergency response.',
      url: f.securityPolicyUrl,
      icon: 'shield',
    });
  }
  if (f.coiRequirementsUrl) {
    out.push({
      title: 'Insurance (COI) Requirements',
      description: 'What your certificate of insurance needs to show, and how to send it in.',
      url: f.coiRequirementsUrl,
      icon: 'file',
    });
  }
  // THE STUDIO'S OWN, last because they are the studio's additions to a shelf
  // whose first entries are the ones every studio has.
  for (const d of f.studioDocs) {
    // An uploaded file is signed at view time; a pasted link is used as-is. A
    // signature that did not come back means the file is gone or not ours —
    // the row is dropped rather than rendered as a card that opens nothing.
    const href = d.path ? signedFor(d.path) : d.url;
    if (href) out.push({ title: d.title, description: 'From your studio.', url: href, icon: 'file' });
  }
  return out;
}

/** HUB-066: links live an hour; they are re-signed every 45 minutes while the page is open. */
export const SIGNED_LINK_LIFETIME_S = 3600;
export const SIGNED_LINK_RENEW_MS = 45 * 60 * 1000;

/** HUB-071: the owner's documents that did not open, named. */
export function unopenedSentence(titles: string[]): string {
  const list = titles.length <= 3 ? titles.join(', ') : `${titles.slice(0, 3).join(', ')} and ${titles.length - 3} more`;
  return `${titles.length === 1 ? 'One document' : `${titles.length} documents`} your owner added could not be opened just now: ${list}. Ask your owner to check ${titles.length === 1 ? 'it' : 'them'}, or try again.`;
}

export default function Documents() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const { activeLocationId } = useActiveLocationId();

  // Which studio's documents — the active location, else this pro's tenancy.
  const { data: resolvedLocation } = useQuery({
    queryKey: ['documents-location', userId, activeLocationId],
    enabled: !!userId,
    queryFn: async () => {
      if (activeLocationId) return activeLocationId;
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/users/{authUserId}/documents-location — both reads below in one.
        const { data } = await cellGet<{ pro: { id: string } | null; tenant: { location_id: string | null } | null }>(
          `/v1/pro-hub/users/${userId!}/documents-location`);
        return data?.tenant?.location_id ?? null;
      }
      const { data: pro } = await supabase
        .from('pros').select('id').eq('auth_user_id', userId!).maybeSingle();
      if (!pro) return null;
      const { data: tenant } = await supabase
        .from('tenants').select('location_id').eq('pro_id', pro.id)
        .order('updated_at', { ascending: false }).limit(1).maybeSingle();
      return tenant?.location_id ?? null;
    },
  });

  const { data: facts, isLoading, isError: factsFailed, refetch: refetchFacts } = useStudioFacts(resolvedLocation);

  // One batch, one round trip — see lib/proLibrary.ts on why links are minted
  // up front rather than per click.
  const studioDocPaths = (facts?.studioDocs ?? []).map((d) => d.path).filter(Boolean) as string[];
  const { data: signed, isError: signFailed, refetch: refetchSigned } = useQuery({
    // Keyed on the paths too: an owner adding a document must not need a hard
    // reload before their pros can open it.
    queryKey: ['pro-library', resolvedLocation, studioDocPaths.join('|')],
    enabled: !!userId,
    // Comfortably inside the hour the signatures last.
    staleTime: SIGNED_LINK_RENEW_MS,
    // HUB-066: stale alone never re-runs a mounted query, so a page left open past the hour held
    // dead links. Re-sign on a timer inside the hour, and on return to the tab (a laptop that slept
    // through the timer comes back stale and is re-signed on focus).
    refetchInterval: SIGNED_LINK_RENEW_MS,
    refetchOnWindowFocus: true,
    queryFn: () => signLibrary(libraryPaths(resolvedLocation ?? null, studioDocPaths), SIGNED_LINK_LIFETIME_S),
  });
  const loadFailed = factsFailed || signFailed;
  const signedFor = (path?: string) => (path && signed ? signed[path] ?? null : null);
  // HUB-071 (partly round, item a8d0d416): a file the OWNER added whose signature did not come back (a per-file
  // error: the file is gone, or this pro may not read it) used to be dropped in silence — and if every one failed the
  // shelf said "Your owner hasn't added anything yet". The owner DID add them: they are named as not opening.
  const unopened = signed && !signFailed
    ? (facts?.studioDocs ?? []).filter((d) => d.path && !signed[d.path]).map((d) => d.title)
    : [];

  const docs = facts
    ? docsFromStudio(facts, resolvedLocation ? signedFor(handbookPath(resolvedLocation)) : null, signedFor)
    : [];
  // THE STUDIO'S LINE, NOT THE OWNER'S MOBILE. My Studio's "Support phone" is
  // the field labelled how pros reach you; location_configs.phone (what this
  // read before) was Peter's personal number on every pro's screen.
  const locationPhone = facts?.supportPhone ?? null;

  // ONE PLACE THE SHELF IS DESCRIBED, so the folds, the counts and the order
  // cannot drift from each other.
  const sections = [
    {
      key: 'studio',
      title: 'Your studio',
      note: "Your studio's own paperwork",
      items: docs.map((d) => ({
        title: d.title, description: d.description, href: d.url,
        icon: d.icon ?? 'book' as DocIcon, verb: 'Read', download: undefined as string | undefined,
      })),
      // HUB-071: "nothing added" only when the reads worked; a failed read says so instead.
      empty: loadFailed || unopened.length ? "Your studio's documents could not be loaded just now." : "Your owner hasn't added anything yet.",
    },
    // THIS STUDIO'S OWN TALK, if it has one. talksFor() returns [] for every
    // studio without an entry, so the whole section simply does not render —
    // Sharon's pros see Peter's talk, nobody else's shelf changes.
    ...(talksFor(resolvedLocation ?? null).length
      ? [{
          key: 'talks',
          title: 'Watch & learn',
          note: 'From IMAGE Studios',
          items: talksFor(resolvedLocation ?? null)
            .map((i) => ({ i, href: i.url ?? signedFor(i.path) }))
            .filter((x) => !!x.href)
            .map(({ i, href }) => ({
              title: i.title, description: i.description, href: href!,
              icon: 'star' as DocIcon,
              verb: i.kind === 'watch' ? 'Watch' : i.kind === 'listen' ? 'Listen' : 'Read',
              download: undefined as string | undefined,
            })),
          empty: null as string | null,
        }]
      : []),
    ...IMAGE_LIBRARY.map((g) => ({
      key: g.group,
      title: g.group,
      note: 'From IMAGE',
      items: g.items
        .map((i) => ({ i, href: i.url ?? signedFor(i.path) }))
        .filter((x) => !!x.href)
        .map(({ i, href }) => ({
          title: i.title, description: i.description, href: href!,
          icon: (i.kind === 'download' ? 'file' : i.kind === 'read' ? 'book' : 'star') as DocIcon,
          verb: i.kind === 'download' ? 'Download' : i.kind === 'watch' ? 'Watch' : i.kind === 'listen' ? 'Listen' : 'Read',
          download: i.kind === 'download' ? i.path?.split('/').pop() : undefined,
        })),
      empty: null as string | null,
    })),
  ];

  return (
    <div className="space-y-3">
      <div>
        <h1 className="font-display text-2xl font-bold text-image-black">
          <span className="text-image-pink">Documents</span>
        </h1>
        <p className="text-sm text-image-charcoal mt-1">
          Your studio's paperwork and the IMAGE Pro library. Tap a section to open it.
        </p>
      </div>

      {loadFailed && (
        <div role="alert" className="card !py-3 text-sm text-red-700 flex items-center justify-between gap-3" data-testid="docs-load-failed">
          <span>Some of your documents could not be opened just now — this is a connection problem, not an empty shelf.</span>
          <button type="button" className="btn-ghost text-sm shrink-0"
            onClick={() => { if (factsFailed) void refetchFacts(); void refetchSigned(); }}>
            Try again
          </button>
        </div>
      )}
      {!loadFailed && unopened.length > 0 && (
        <div role="alert" className="card !py-3 text-sm text-red-700 flex items-center justify-between gap-3" data-testid="docs-unopened">
          <span>{unopenedSentence(unopened)}</span>
          <button type="button" className="btn-ghost text-sm shrink-0" onClick={() => void refetchSigned()}>
            Try again
          </button>
        </div>
      )}
      {isLoading ? (
        <div className="text-sm text-image-charcoal py-6 text-center">Loading documents…</div>
      ) : (
        /* FOLDS, NOT A SCROLL (Peter, 2026-08-26: "it's just taking a lot of
           space, a lot of scrolling... I need the space for that to show up").
           Thirteen full-width cards was four screens of thumb; the same shelf
           as titled folds is one. YOUR STUDIO opens by default because it is
           the pro's own studio and the place its own documents will appear;
           IMAGE's library sits closed until asked for. The chevron fold is the
           product's own idiom, not a new invention. */
        <div className="space-y-2">
          {sections.map((sec, idx) => (
            <Fold
              key={sec.key}
              title={sec.title}
              note={sec.note}
              count={sec.items.length}
              defaultOpen={idx === 0}
              empty={sec.empty}
            >
              {sec.items.map((item) => (
                <DocRow key={item.title} {...item} />
              ))}
            </Fold>
          ))}
        </div>
      )}

      {locationPhone && (
        <p className="text-xs text-image-charcoal text-center pt-3">
          Questions about any policy? Call or text{' '}
          <a href={`tel:${locationPhone}`} className="font-semibold text-image-black hover:text-image-pink">
            {formatPhone(locationPhone)}
          </a>
        </p>
      )}
    </div>
  );
}

/**
 * A titled fold. Closed sections cost one line each, which is the whole point:
 * a pro opens what they came for instead of scrolling past what they didn't.
 */
function Fold({ title, note, count, defaultOpen, empty, children }: {
  title: string; note: string; count: number; defaultOpen?: boolean;
  empty: string | null; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="card !p-0 overflow-hidden border border-image-border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-image-pink-soft/20 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <h2 className="ph-sec">
            {title}{' '}
            <span className="text-image-charcoal font-normal">({count})</span>
          </h2>
          <p className="text-[11px] text-image-charcoal mt-0.5">{note}</p>
        </div>
        {/* A chevron TURNS — it says open/closed. It is not an arrow pointing at
            a link, which is the thing Peter has asked to be rid of. */}
        <svg className={`size-4 flex-none text-image-charcoal transition-transform ${open ? 'rotate-180' : ''}`}
             viewBox="0 0 16 16" fill="none" aria-hidden>
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="border-t border-image-border">
          {count === 0
            ? <p className="text-xs text-image-charcoal px-4 py-4">{empty}</p>
            : children}
        </div>
      )}
    </div>
  );
}

/**
 * One document. A compact row, not a card — the title, one line about it, and
 * the verb. NO ARROW: Peter, twice now, "anyone come in here will click
 * something. They don't need an arrow to click it." The whole row is the
 * target; the verb says what happens, which an arrow never did.
 */
function DocRow({ title, description, href, icon, verb, download }: {
  title: string; description: string; href: string; icon: DocIcon; verb: string; download?: string;
}) {
  return (
    <a
      href={href}
      {...(download ? { download } : { target: '_blank', rel: 'noopener noreferrer' })}
      className="flex items-start gap-3 px-4 py-3 border-b border-image-border last:border-b-0 hover:bg-image-pink-soft/20 transition-colors"
    >
      <div className="size-8 rounded-lg bg-image-pink-soft/40 flex items-center justify-center shrink-0 text-image-plum mt-0.5">
        <DocIconSwitch icon={icon} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <h3 className="font-semibold text-image-black text-sm leading-snug">{title}</h3>
          <span className="text-image-pink text-[11px] font-semibold shrink-0">{verb}</span>
        </div>
        <p className="text-xs text-image-charcoal leading-relaxed mt-0.5">{description}</p>
      </div>
    </a>
  );
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

function DocIconSwitch({ icon }: { icon: DocIcon }) {
  switch (icon) {
    case 'shield': return <ShieldIcon />;
    case 'file':   return <FileIcon />;
    case 'star':   return <StarIcon />;
    case 'book':
    default:       return <BookIcon />;
  }
}

function BookIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15 8.5 22 9.3 17 14.1 18.2 21 12 17.8 5.8 21 7 14.1 2 9.3 9 8.5" />
    </svg>
  );
}
